//============================================================================
// Name        : receiveReducer.ts
// Purpose     : Define store Receive reducer
// Created     : 2019-07-11
// Author      : Senitron Team
// Copyright   : Copyright 2019 Senitron Corporation. All rights reserved.
//============================================================================

import moment from "moment";
import {
    addLot,
    beginReception,
    cancelPrinting,
    cancelReceiveItem,
    cancelReception,
    confirmCancelPrint,
    continueReception,
    fetchItemInfo,
    finishReceivingItem,
    finishReception,
    hideCancelConfirmation,
    hideCancelItemConfirmation,
    hideCancelPrint,
    hideErrorDialog,
    hideReprintDialog,
    IAddLotAction,
    IBeginReceptionAction,
    ICancelReceiveAction,
    IFetchItemInfoAction,
    IFinishReceivingItemAction,
    IFinishReceptionAction,
    IReceptionAction,
    IRemoveLotAction,
    ISaveGeneratedLabelsAction,
    ISetErrorDismissCallbackAction,
    ISetItemStorageUnitsAction,
    ISetLabelVerifiedAction, IShowCancelConfirmationAction, IShowCancelItemConfirmationAction, IShowChangeDateConfirmationAction,
    IShowErrorDialogAction,
    IShowUndoItemConfirmationAction,
    IShowUndoProcessAction,
    IUpdateLotAction,
    IUpdateStateAction,
    printLabels,
    removeLot,
    saveGeneratedLabels,
    setErrorDismissCallback,
    setItemStorageUnits,
    setLabelVerified,
    showCancelConfirmation,
    showCancelItemConfirmation, showChangeDateConfirmation,
    showErrorDialog,
    showReprintDialog,
    showUndoItemConfirmation,
    showUndoProcess,
    updateLot,
    updateState
} from "../actions/receiveActions";
import {IEPCEncodingInfo, IItem, IReceiveProps, ReceiveActions, ReceiveStatuses} from "../types";

const initialState: Partial<IReceiveProps> = {
    reception: {} as IItem,
    currentItemId: null,
    printers: [],
    epcEncodingInfo: {} as IEPCEncodingInfo,
    allowedStartDate: "",
    allowedEndDate: "",
    qtyTolerance: 0,
    qtyToleranceUnder: 0,
    priceFluctuationThreshold: 0,

    // Item properties
    postingDate: moment(new Date()).format("YYYY-MM-DD"),
    validDates: false,
    isReceiving: false,

    // Virtual Properties
    receivingId: "",
    vendorName: "",
    status: ReceiveStatuses.Pending,

    // Confirmation Dialog
    showConfirmation: false,
    showItemConfirmation: false,
    confirmationTitle: "",
    confirmationMsg: "",
    confirmationDlgPositiveCallback: null,
    confirmationDlgNegativeCallback: null,

    // Error Dialog
    showError: false,
    errorTitle: "",
    errorMsg: "",
    dismissErrorCallback: null,

    // Printing Dialog
    showPrintDialog: false,
    showCancelPrintConfirm: false,
    showReprintDialog: false,
    validatePasswordUrl: ""
};

export default function(state = initialState, action: IReceptionAction): IReceiveProps {
    switch (action.type) {

        //====================================================================
        // Map Actions messages to Action functions
        //====================================================================

        case ReceiveActions.StoreReceiveProps:          return updateState(state, action as IUpdateStateAction);
        case ReceiveActions.UpdateState:                return updateState(state, action as IUpdateStateAction);
        case ReceiveActions.Continue:                   return continueReception(state);
        case ReceiveActions.ShowErrorDialog:            return showErrorDialog(state, action as IShowErrorDialogAction);
        case ReceiveActions.HideErrorDialog:            return hideErrorDialog(state);
        case ReceiveActions.SetErrorDismissCallback:    return setErrorDismissCallback(state, action as ISetErrorDismissCallbackAction);
        case ReceiveActions.CancelReceive:              return cancelReception(state);
        case ReceiveActions.BeginReception:             return beginReception(state, action as IBeginReceptionAction);
        case ReceiveActions.ShowCancelConfirmation:     return showCancelConfirmation(state, action as IShowCancelConfirmationAction);
        case ReceiveActions.ShowCancelItemConfirmation: return showCancelItemConfirmation(state, action as IShowCancelItemConfirmationAction);
        case ReceiveActions.ShowUndoItemConfirmation:   return showUndoItemConfirmation(state, action as IShowUndoItemConfirmationAction);
        case ReceiveActions.HideCancelConfirmation:     return hideCancelConfirmation(state);
        case ReceiveActions.HideCancelItemConfirmation: return hideCancelItemConfirmation(state);
        case ReceiveActions.CancelReceiveItem:          return cancelReceiveItem(state, action as ICancelReceiveAction);
        case ReceiveActions.FetchItemInfo:              return fetchItemInfo(state, action as IFetchItemInfoAction);
        case ReceiveActions.SetItemStorageUnits:        return setItemStorageUnits(state, action as ISetItemStorageUnitsAction);
        case ReceiveActions.AddLot:                     return addLot(state, action as IAddLotAction);
        case ReceiveActions.UpdateLot:                  return updateLot(state, action as IUpdateLotAction);
        case ReceiveActions.RemoveLot:                  return removeLot(state, action as IRemoveLotAction);
        case ReceiveActions.PrintLabels:                return printLabels(state);
        case ReceiveActions.ConfirmCancelPrint:         return confirmCancelPrint(state);
        case ReceiveActions.HideCancelPrint:            return hideCancelPrint(state);
        case ReceiveActions.CancelPrinting:             return cancelPrinting(state);
        case ReceiveActions.SaveGeneratedLabels:        return saveGeneratedLabels(state, action as ISaveGeneratedLabelsAction);
        case ReceiveActions.SetLabelVerified:           return setLabelVerified(state, action as ISetLabelVerifiedAction);
        case ReceiveActions.FinishReceivingItem:        return finishReceivingItem(state, action as IFinishReceivingItemAction);
        case ReceiveActions.FinishReception:            return finishReception(state, action as IFinishReceptionAction);
        case ReceiveActions.ShowReprintDialog:          return showReprintDialog(state);
        case ReceiveActions.HideReprintDialog:          return hideReprintDialog(state);
        case ReceiveActions.ShowChangeDateConfirmation: return showChangeDateConfirmation(state, action as IShowChangeDateConfirmationAction);
        case ReceiveActions.ShowUndoProcess:            return showUndoProcess(state, action as IShowUndoProcessAction);

        //====================================================================

        default:
            return state as IReceiveProps;
    }
}
